import {
  AutocompleteInput,
  Datagrid,
  Filter,
  FunctionField,
  PaginationLimit,
  ReferenceField,
  ReferenceInput,
  ResourceContextProvider,
  SearchInput,
  TextField,
} from "react-admin";
import React from "react";
import CustomPriceField from "../../../components/CustomPriceField";
import EmptyComponent from "./EmptyComponent";
import StatusField from "../../../components/StatusField";
import ApproveButton from "./ApproveButton";
import RejectButton from "./RejectButton";
import BulkConfirmButton from "./BulkConfirmButton";
import { availableOnlySuperAdmin } from "utils/validation";
import { PaddingDenseStyledList } from "../../../components/ListWrapper";
import { StyledButton } from "../../../components/ListShowButton";
import ActionsMenu from "../../../components/ActionsMenu";
import CustomPagination from "../../../components/CustomPagination";
import UpdatedByField from "../../../components/UpdatedByField";
import DeleteButton from "./DeleteButton";

interface IUnapprovedInvoiceTable {
  subcontractorId?: number;
  hasIssued?: boolean;
  isFixedHeight?: boolean;
  status?: "Pending" | "Approved" | "Rejected";
  isShowTitle?: boolean;
}

const SubcontractorAdhocFeeFilter = (props: any) => (
  <Filter {...props}>
    <SearchInput source="search" alwaysOn />

    {props.isNotFilteredBySubcontractor && (
      <ReferenceInput
        reference="subcontractors"
        source="SubcontractorId"
        filterToQuery={(searchText) => {
          if (searchText !== "") return { search: searchText };
        }}
        sort={{ field: "name", order: "ASC" }}
        alwaysOn
      >
        <AutocompleteInput optionText="name" />
      </ReferenceInput>
    )}

    <ReferenceInput
      source="SchoolId"
      reference="schools"
      filterToQuery={(searchText) => {
        if (searchText !== "") return { search: searchText };
      }}
      sort={{ field: "name", order: "ASC" }}
      alwaysOn
    >
      <AutocompleteInput optionText="name" />
    </ReferenceInput>
  </Filter>
);

const SubcontractorAdhocFeeTable = ({
  subcontractorId,
  hasIssued,
  isFixedHeight,
  status,
  isShowTitle,
}: IUnapprovedInvoiceTable) => {
  const isNotFilteredBySubcontractor = !subcontractorId;
  const isPending = status === "Pending";

  const SubcontractorAdhocFeeEditButton = (record: ISubcontractorAdhocFee) => (
    <StyledButton
      variant="outlined"
      href={`/#/subcontractorAdhocFees/${record.id}?subcontractorId=${record.subcontractorId}`}
    >
      Edit
    </StyledButton>
  );

  return (
    <ResourceContextProvider value="subcontractorAdhocFees">
      <PaddingDenseStyledList
        basePath="/subcontractorAdhocFees"
        actions={null}
        exporter={false}
        hasCreate={false}
        filters={
          <SubcontractorAdhocFeeFilter
            isNotFilteredBySubcontractor={isNotFilteredBySubcontractor}
          />
        }
        filterDefaultValues={{
          SubcontractorId: subcontractorId,
          HasIssued: hasIssued,
          Status: status,
        }}
        sort={{ field: "id", order: "DESC" }}
        bulkActionButtons={
          !hasIssued &&
          availableOnlySuperAdmin() && (
            <BulkConfirmButton
              subcontractorId={subcontractorId}
              isPending={isPending}
              status={status}
            />
          )
        }
        title={isShowTitle ? "Subcontractor Adhoc Fees" : " "}
        pagination={
          <CustomPagination
            limit={isFixedHeight ? <EmptyComponent /> : <PaginationLimit />}
          />
        }
      >
        <Datagrid>
          <TextField source="id" emptyText="-" />
          <ReferenceField
            source="subcontractorId"
            label="Subcontractor Identifier"
            reference="subcontractors"
            link={false}
          >
            <TextField source="subcontractorIdentifier" emptyText="-" />
          </ReferenceField>
          <ReferenceField
            source="subcontractorId"
            label="Subcontractor MBC Code"
            reference="subcontractors"
            link={false}
          >
            <TextField source="mbcVendorId" emptyText="-" />
          </ReferenceField>
          <ReferenceField
            source="subcontractorId"
            reference="subcontractors"
            link={false}
          >
            <TextField source="name" emptyText="-" />
          </ReferenceField>
          <ReferenceField source="schoolId" label="School" reference="schools">
            <TextField source="name" />
          </ReferenceField>
          <TextField source="routeName" emptyText="-" />
          <TextField source="category" emptyText="-" />
          <TextField source="period" emptyText="-" />
          <CustomPriceField source="amount" />
          <TextField source="remarks" emptyText="-" />
          <StatusField source="status" />
          <FunctionField
            label="Updated By"
            render={(record: any) => (
              <>
                {record.updatedBy === null ? (
                  "-"
                ) : (
                  <UpdatedByField data={record.updatedBy} />
                )}
              </>
            )}
          />

          {!hasIssued && (
            <FunctionField
              label="Actions"
              render={(record: any) => (
                <ActionsMenu
                  numberOfActions={4}
                  render={[
                    availableOnlySuperAdmin() && isPending && (
                      <ApproveButton id={record.id} />
                    ),
                    availableOnlySuperAdmin() && isPending && (
                      <RejectButton id={record.id} />
                    ),
                    availableOnlySuperAdmin() && (
                      <DeleteButton id={record.id} status={status} />
                    ),
                    isPending && (
                      <SubcontractorAdhocFeeEditButton {...record} />
                    ),
                  ]}
                />
              )}
            />
          )}
        </Datagrid>
      </PaddingDenseStyledList>
    </ResourceContextProvider>
  );
};

export default SubcontractorAdhocFeeTable;
